import { useState } from "react";
import { usePendingAmendments } from "../hooks/useSowingLifecycle";
import { DecideAmendmentDialog } from "../components/sowingInstructions/SowingLifecycleDialogs";
import type { SowingInstructionAmendmentDto } from "../services/sowingLifecycleApi";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";

export const AmendmentApprovalPage = () => {
  const amendmentsQuery = usePendingAmendments();
  const [selected, setSelected] = useState<SowingInstructionAmendmentDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDecide = (amendment: SowingInstructionAmendmentDto) => {
    setSelected(amendment);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    setSelected(null);
    amendmentsQuery.refetch();
  };

  const rows = amendmentsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Amendment Approvals</h1>
        <p className="text-muted-foreground mt-1">
          Sowing instruction amendments proposed by the Sowing Manager awaiting your decision.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="sowing_instructions.approve" />
        </div>
      </div>

      {amendmentsQuery.isLoading && (
        <div className="flex items-center justify-center h-64">
          <p>Loading amendments...</p>
        </div>
      )}

      {amendmentsQuery.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load pending amendments</p>
        </div>
      )}

      {!amendmentsQuery.isLoading && rows.length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">No pending amendments to review</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instruction #</TableHead>
                <TableHead>Proposed By</TableHead>
                <TableHead>New Qty</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((amendment) => (
                <TableRow key={amendment.id}>
                  <TableCell className="font-mono text-sm">
                    {amendment.instructionNumber ?? "—"}
                  </TableCell>
                  <TableCell>{amendment.amendedByUserName ?? "—"}</TableCell>
                  <TableCell>
                    <span className="text-muted-foreground line-through mr-1">
                      {amendment.priorQuantityToSow}
                    </span>
                    <span className="font-semibold">{amendment.newQuantityToSow}</span>
                  </TableCell>
                  <TableCell>
                    {format(parseISO(amendment.newScheduledStartDate), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>
                    {format(parseISO(amendment.newScheduledEndDate), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">
                    {amendment.reason}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {format(parseISO(amendment.amendedAt), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => handleDecide(amendment)}>
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {selected && (
        <DecideAmendmentDialog
          amendment={selected}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};
